import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Star, Calendar, Briefcase, Clock, 
  Ruler, Weight, Dumbbell, UserCheck, MessageSquare, 
  UserPlus, CheckCircle, ChevronRight, Award, Shield, Users, List, Plus
} from 'lucide-react';
import { getWorkerById } from '../api/workers';
import { createDirectHireOffer, getOpenJobsForBuyer } from '../api/jobs';
import { inviteWorkerToRoster } from '../api/broker';
import { useAuth } from '../context/AuthContext';
import loadImage from '../assets/imageLoader';

const WorkerPublicProfile = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');

  // Hire Modal State
  const [isHireModalOpen, setIsHireModalOpen] = useState(false);
  const [hireType, setHireType] = useState('new'); // 'new' or 'existing'
  const [existingJobs, setExistingJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  
  const [hireFormData, setHireFormData] = useState({
    title: '',
    description: '',
    job_type: 'fixed_contract',
    budget_per_worker: '',
    estimated_duration_hours: '',
    start_date: ''
  });
  const [hireLoading, setHireLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await getWorkerById(profileId);

        const p = data.profile || {};
        const loc = p.location || {};
        // Check both top level and profile level for ratings
        const ratings = data.received_ratings || p.received_ratings || [];
        const locationString = [loc.upazila, loc.district, loc.division].filter(Boolean).join(', ') || 'Location not specified';

        setWorker({
          id: data.id,
          workerProfileId: p.id,
          name: p.name || 'N/A',
          location: locationString,
          memberSince: data.createdAt,
          avatarUrl: p.picture,
          overallRating: parseFloat(data.overallRating) || 0,
          reviewCount: parseInt(data.reviewCount) || 0,
          availability: data.availability || 'available',
          isBroker: data.isBroker,
          bio: 'No biography available.',
          skills: data.categories ? data.categories.map(c => c.Name) : [],
          equipment: p.owned_instruments ? p.owned_instruments.map(i => i.name) : [],
          team: data.team || [],
          height: data.height ? `${data.height} ft` : 'N/A',
          weight: data.weight ? `${data.weight} kg` : 'N/A',
          strength: data.strength ? `${data.strength} kg` : 'N/A',
          experience_year: data.experience_year || 0,
          reliabilityScore: data.reliability_score || 100,
          is_Identity_Verified: p.User?.is_Identity_Verified,
          totalJobsCompleted: data.totalJobsCompleted,
          totalWorkingHours: data.totalWorkingHours,
          workHistory: ratings.map(r => ({
              id: r.id,
              employer: r.rater?.name || 'Verified Customer',
              picture: r.rater?.picture,
              date: new Date(r.createdAt).toLocaleDateString(),
              rating: r.rating,
              review: r.review
          }))
          });

      } catch (err) {
        setError('Failed to fetch worker profile.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profileId]);

  // Fetch existing jobs when modal opens
  useEffect(() => {
    if (isHireModalOpen && user?.role === 'buyer') {
      const fetchJobs = async () => {
        try {
          const jobs = await getOpenJobsForBuyer();
          setExistingJobs(jobs);
        } catch (err) {
          console.error("Failed to fetch existing jobs:", err);
        }
      };
      fetchJobs();
    }
  }, [isHireModalOpen, user]);

  const handleHireMe = () => setIsHireModalOpen(true);

  const handleHireInputChange = (e) => {
    const { name, value } = e.target;
    setHireFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitHire = async (e) => {
    e.preventDefault();
    setHireLoading(true);
    try {
      const payload = {
        workerProfileId: worker.workerProfileId
      };

      if (hireType === 'existing') {
        if (!selectedJobId) {
          alert('Please select a job.');
          setHireLoading(false);
          return;
        }
        payload.existingJobId = selectedJobId;
      } else {
        Object.assign(payload, hireFormData);
      }

      await createDirectHireOffer(payload);
      alert('Job offer sent successfully!');
      setIsHireModalOpen(false);
      // Reset form
      setHireFormData({
        title: '',
        description: '',
        job_type: 'fixed_contract',
        budget_per_worker: '',
        estimated_duration_hours: '',
        start_date: ''
      });
      setSelectedJobId('');
      setHireType('new');
    } catch (err) {
      console.error("Failed to send hire offer:", err);
      alert(err.response?.data?.message || 'Failed to send job offer. Please try again.');
    } finally {
      setHireLoading(false);
    }
  };

  const handleInviteToRoster = async () => {
    if (!window.confirm(`Are you sure you want to invite ${worker.name} to your roster?`)) return;
    try {
      await inviteWorkerToRoster(worker.workerProfileId);
      alert('Invitation sent successfully!');
    } catch (err) {
      console.error("Failed to invite worker:", err);
      const message = err.response?.data?.message || 'Failed to send invitation.';
      alert(message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-slate-50">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error || !worker) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-slate-500">
        <p className="text-xl mb-4">{error || 'Worker not found'}</p>
        <button onClick={() => navigate(-1)} className="text-brand-primary hover:underline">Go Back</button>
      </div>
    );
  }

  const tabVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans pb-12">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-brand-dark to-brand-primary h-64 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-slate-50/20 to-transparent" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 -mt-24 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8 items-start">
            {/* Avatar */}
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="relative shrink-0 mx-auto md:mx-0"
            >
              <div className="w-40 h-40 rounded-full p-1 bg-white shadow-lg">
                <img
                  src={loadImage(worker.avatarUrl)}
                  alt={worker.name}
                  className="w-full h-full rounded-full object-cover border-4 border-brand-light"
                />
              </div>
              <div className={`absolute bottom-3 right-3 w-6 h-6 rounded-full border-4 border-white ${worker.availability === 'available' ? 'bg-green-500' : 'bg-yellow-500'}`} />
            </motion.div>

            {/* Main Info */}
            <div className="flex-grow text-center md:text-left space-y-3">
              <div className="flex flex-col md:flex-row md:items-center gap-2 justify-center md:justify-start">
                <h1 className="text-3xl font-bold text-slate-900">{worker.name}</h1>
                <div className="flex gap-2">
                    {worker.isBroker && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Shield className="w-3 h-3 mr-1" />
                        Broker
                    </span>
                    )}
                    {worker.is_Identity_Verified && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                    </span>
                    )}
                </div>
              </div>
              
              <div className="flex items-center justify-center md:justify-start text-slate-600 gap-2">
                <MapPin className="w-4 h-4" />
                <span>{worker.location}</span>
              </div>

              <div className="flex items-center justify-center md:justify-start gap-4 text-sm text-slate-500">
                <div className="flex items-center gap-1 text-yellow-500 font-medium">
                  <Star className="w-4 h-4 fill-current" />
                  <span>{worker.overallRating.toFixed(1)}</span>
                  <span className="text-slate-400 font-normal">({worker.reviewCount} reviews)</span>
                </div>
                <span className="hidden md:inline">•</span>
                <div className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  <span>{worker.totalJobsCompleted} Jobs</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 flex flex-wrap gap-3 justify-center md:justify-start">
                {!worker.isBroker && user?.role === 'buyer' && (
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleHireMe}
                    className="flex items-center gap-2 bg-brand-primary text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-brand-dark transition-colors shadow-lg shadow-brand-primary/20"
                  >
                    <UserCheck className="w-5 h-5" />
                    Hire Me
                  </motion.button>
                )}

                {!worker.isBroker && user?.role === 'broker' && (
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleInviteToRoster}
                    className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
                  >
                    <UserPlus className="w-5 h-5" />
                    Invite to Roster
                  </motion.button>
                )}

                {user?.profileId !== worker.workerProfileId && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/messages', { state: { startChatWith: { id: worker.workerProfileId, name: worker.name, picture: worker.avatarUrl } } })}
                    className="flex items-center gap-2 bg-white text-slate-700 font-semibold py-2.5 px-6 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    <MessageSquare className="w-5 h-5" />
                    Message
                  </motion.button>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-t border-slate-100 px-6 md:px-8 mt-6">
            <div className="flex gap-6 overflow-x-auto pb-px">
              {['about', 'reviews', 'equipment', ...(worker.isBroker ? ['team'] : [])].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`relative pb-4 text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                    activeTab === tab ? 'text-brand-primary' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab}
                  {activeTab === tab && (
                    <motion.div 
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary" 
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          
          {/* Left Sidebar: Stats & Details */}
          <div className="space-y-6">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-slate-100"
            >
              <h3 className="text-lg font-bold text-slate-800 mb-4">Physical Attributes</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Ruler className="w-5 h-5 text-brand-secondary" />
                    <span>Height</span>
                  </div>
                  <span className="font-semibold text-slate-800">{worker.height}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Weight className="w-5 h-5 text-brand-secondary" />
                    <span>Weight</span>
                  </div>
                  <span className="font-semibold text-slate-800">{worker.weight}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Dumbbell className="w-5 h-5 text-brand-secondary" />
                    <span>Strength</span>
                  </div>
                  <span className="font-semibold text-slate-800">{worker.strength}</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-slate-100"
            >
              <h3 className="text-lg font-bold text-slate-800 mb-4">Performance</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-brand-light/30 rounded-xl">
                  <div className="text-2xl font-bold text-brand-dark">{worker.totalJobsCompleted}</div>
                  <div className="text-xs text-brand-dark/70 uppercase tracking-wide mt-1">Jobs Done</div>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-xl">
                  <div className="text-2xl font-bold text-emerald-700">{worker.reliabilityScore}%</div>
                  <div className="text-xs text-emerald-600/70 uppercase tracking-wide mt-1">Reliability</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <div className="text-2xl font-bold text-blue-900">{worker.totalWorkingHours}</div>
                  <div className="text-xs text-blue-800/70 uppercase tracking-wide mt-1">Hours</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-xl font-bold text-slate-700">{worker.overallRating.toFixed(1)}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">Rating</div>
                </div>
                <div className="col-span-2 text-center p-4 bg-slate-50 rounded-xl flex items-center justify-center gap-2">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-600 text-sm">Member since {new Date(worker.memberSince).getFullYear()}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {activeTab === 'about' && (
                <motion.div 
                  key="about" 
                  variants={tabVariants} 
                  initial="hidden" 
                  animate="visible" 
                  exit="exit"
                  className="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-slate-100"
                >
                  <h3 className="text-xl font-bold text-slate-900 mb-4">About Me</h3>
                  <p className="text-slate-600 leading-relaxed mb-8">{worker.bio}</p>
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Skills & Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {worker.skills.map((skill, index) => (
                      <span key={index} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium border border-slate-200">
                        {skill}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {activeTab === 'reviews' && (
                <motion.div 
                  key="reviews"
                  variants={tabVariants} 
                  initial="hidden" 
                  animate="visible" 
                  exit="exit"
                  className="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-slate-100"
                >
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-xl font-bold text-slate-900">Employer Reviews</h3>
                    <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-xl border border-slate-100">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="font-bold text-slate-700">{worker.overallRating.toFixed(1)}</span>
                        <span className="text-slate-400 text-xs">({worker.reviewCount} total)</span>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {worker.workHistory.length > 0 ? worker.workHistory.map((job, idx) => (
                      <div key={idx} className="p-6 bg-slate-50/50 rounded-[2rem] border border-slate-100 transition-all hover:bg-white hover:shadow-lg group">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                            <div className="flex items-center gap-3">
                                <img src={loadImage(job.picture)} className="w-10 h-10 rounded-xl object-cover border border-white shadow-sm" alt="Employer" />
                                <div>
                                    <h4 className="text-base font-black text-slate-800 leading-tight">{job.employer}</h4>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{job.date}</p>
                                </div>
                            </div>
                            <div className="flex text-yellow-400 gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={14} className={i < job.rating ? 'fill-current' : 'text-slate-200'} />
                                ))}
                            </div>
                        </div>
                        <div className="relative pl-4 border-l-2 border-emerald-500/20 py-1">
                          <p className="text-slate-600 font-medium leading-relaxed italic text-sm md:text-base">
                            "{job.review || 'No written feedback was provided.'}"
                          </p>
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-16 bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200">
                        <Star className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-400 font-bold">No reviews yet.</p>
                        <p className="text-xs text-slate-300 uppercase tracking-widest mt-1">Reputation is built over time</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {activeTab === 'equipment' && (
                <motion.div 
                  key="equipment"
                  variants={tabVariants} 
                  initial="hidden" 
                  animate="visible" 
                  exit="exit"
                  className="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-slate-100"
                >
                  <h3 className="text-xl font-bold text-slate-900 mb-6">Equipment Owned</h3>
                  {worker.equipment.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {worker.equipment.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-brand-primary/30 hover:shadow-md transition-all bg-slate-50/50">
                          <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center text-brand-dark">
                            <CheckCircle className="w-5 h-5" />
                          </div>
                          <span className="font-semibold text-slate-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No equipment listed.</p>
                  )}
                </motion.div>
              )}

              {activeTab === 'team' && (
                <motion.div 
                  key="team"
                  variants={tabVariants} 
                  initial="hidden" 
                  animate="visible" 
                  exit="exit"
                  className="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-slate-100"
                >
                  <h3 className="text-xl font-bold text-slate-900 mb-6">Team Members</h3>
                  {worker.team.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {worker.team.map((member) => (
                        <div key={member.id} className="group p-4 rounded-xl border border-slate-200 hover:border-brand-primary hover:shadow-lg transition-all bg-white flex items-center gap-4">
                          <img 
                            src={loadImage(member.picture)} 
                            alt={member.name} 
                            className="w-12 h-12 rounded-full object-cover shadow-sm"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-slate-900 truncate">{member.name}</h4>
                            <p className="text-xs text-slate-500 truncate">{member.skills.join(', ') || 'Worker'}</p>
                          </div>
                          <Link to={`/workers/profile/${member.id}`} className="text-slate-300 hover:text-brand-primary transition-colors">
                            <ChevronRight className="w-5 h-5" />
                          </Link>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p>No team members assigned.</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Hire Modal */}
        <AnimatePresence>
          {isHireModalOpen && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
            >
              <motion.div 
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
              >
                <div className="bg-brand-primary p-6 text-white flex justify-between items-center shrink-0">
                  <h2 className="text-xl font-bold">Hire {worker.name}</h2>
                  <button onClick={() => setIsHireModalOpen(false)} className="text-white/80 hover:text-white transition-colors">
                    ✕
                  </button>
                </div>

                {/* Switcher */}
                <div className="p-4 border-b border-slate-100 flex gap-2">
                  <button 
                    onClick={() => setHireType('new')}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${hireType === 'new' ? 'bg-brand-primary/10 text-brand-primary' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    <Plus className="w-4 h-4" /> Create New Job
                  </button>
                  <button 
                    onClick={() => setHireType('existing')}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${hireType === 'existing' ? 'bg-brand-primary/10 text-brand-primary' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    <List className="w-4 h-4" /> Existing Job
                  </button>
                </div>
                
                <form onSubmit={handleSubmitHire} className="p-6 space-y-5 overflow-y-auto">
                  
                  {hireType === 'new' ? (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Job Title</label>
                        <input
                          type="text"
                          name="title"
                          required
                          value={hireFormData.title}
                          onChange={handleHireInputChange}
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-all"
                          placeholder="e.g. Rice Harvesting"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Description</label>
                        <textarea
                          name="description"
                          required
                          value={hireFormData.description}
                          onChange={handleHireInputChange}
                          rows="3"
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-all resize-none"
                          placeholder="Describe the work details..."
                        ></textarea>
                      </div>

                      <div className="grid grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Job Type</label>
                          <select
                            name="job_type"
                            value={hireFormData.job_type}
                            onChange={handleHireInputChange}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-all"
                          >
                            <option value="fixed_contract">Fixed Contract</option>
                            <option value="hourly">Hourly</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                            {hireFormData.job_type === 'hourly' ? 'Rate/Hour (Tk)' : 'Budget (Tk)'}
                          </label>
                          <input
                            type="number"
                            name="budget_per_worker"
                            required
                            value={hireFormData.budget_per_worker}
                            onChange={handleHireInputChange}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-all"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Start Date</label>
                          <input
                            type="datetime-local"
                            name="start_date"
                            required
                            value={hireFormData.start_date}
                            onChange={handleHireInputChange}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Duration (Hours)</label>
                          <input
                            type="number"
                            name="estimated_duration_hours"
                            value={hireFormData.estimated_duration_hours}
                            onChange={handleHireInputChange}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-all"
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    // Existing Job Selection
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Select Open Job</label>
                      {existingJobs.length > 0 ? (
                        <select 
                          value={selectedJobId}
                          onChange={(e) => setSelectedJobId(e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-all"
                        >
                          <option value="">-- Choose a Job --</option>
                          {existingJobs.map(job => (
                            <option key={job.id} value={job.id}>{job.title} ({job.post_model === 'bidding' ? 'Bidding' : 'Direct'})</option>
                          ))}
                        </select>
                      ) : (
                        <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                          <p>No open jobs found.</p>
                          <Link to="/publish-job" className="text-brand-primary text-sm font-semibold hover:underline mt-2 inline-block">Post a new job first</Link>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3 pt-4 border-t border-slate-100 mt-4">
                    <button
                      type="button"
                      onClick={() => setIsHireModalOpen(false)}
                      className="flex-1 py-3 text-slate-700 bg-slate-100 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={hireLoading}
                      className="flex-1 py-3 bg-brand-primary text-white font-bold rounded-lg hover:bg-brand-dark transition-all shadow-lg shadow-brand-primary/25 disabled:opacity-50 disabled:shadow-none"
                    >
                      {hireLoading ? 'Sending Offer...' : 'Send Offer'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default WorkerPublicProfile;
